import { c as _c } from "react/compiler-runtime";
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical';
import { type JSXConvertersFunction, LinkJSXConverter, RichText } from '@payloadcms/richtext-lexical/react';
interface PayloadRichTextProps {
  className?: string;
  content: unknown;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
function hasRichTextRoot(value: unknown): value is DefaultTypedEditorState {
  return isRecord(value) && isRecord(value.root) && Array.isArray(value.root.children);
}
function getLinkedDoc(value: unknown) {
  if (isRecord(value) && ('handle' in value || 'slug' in value || 'url' in value)) {
    return value;
  }
  return null;
}
function getTrimmedString(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}
function resolvePageHref(linkedDoc: Record<string, unknown>) {
  const slug = getTrimmedString(linkedDoc.slug);
  if (!slug) return '';
  return slug === 'home' ? '/' : `/pages/${slug}`;
}
function resolveHandleHref(linkedDoc: Record<string, unknown>, prefix: string) {
  const handle = getTrimmedString(linkedDoc.handle);
  return handle ? `${prefix}/${handle}` : '';
}
const internalDocHrefResolvers: Record<string, (linkedDoc: Record<string, unknown>) => string> = {
  pages: resolvePageHref,
  'policy-pages': linkedDoc => resolveHandleHref(linkedDoc, '/policies'),
  'shopify-pages': linkedDoc => resolveHandleHref(linkedDoc, '/pages'),
  'shopify-products': linkedDoc => resolveHandleHref(linkedDoc, '/products'),
  'shopify-collections': linkedDoc => resolveHandleHref(linkedDoc, '/collections')
};
function resolveInternalDocHref({
  linkNode
}: {
  linkNode: {
    fields?: {
      doc?: {
        relationTo?: string;
        value?: unknown;
      } | null;
      url?: string;
    };
  };
}) {
  const fallbackHref = linkNode.fields?.url || '#';
  const doc = linkNode.fields?.doc;
  const linkedDoc = getLinkedDoc(doc?.value);
  if (!doc || !linkedDoc) {
    return fallbackHref;
  }
  type InternalRelation = keyof typeof internalDocHrefResolvers;
  const resolver = typeof doc.relationTo === 'string' ? internalDocHrefResolvers[doc.relationTo as InternalRelation] : undefined;
  const resolvedHref = resolver?.(linkedDoc);
  if (resolvedHref) {
    return resolvedHref;
  }
  if (typeof linkedDoc.url === 'string' && linkedDoc.url.length > 0) {
    return linkedDoc.url;
  }
  return fallbackHref;
}
const storefrontRichTextConverters: JSXConvertersFunction = ({
  defaultConverters
}) => ({
  ...defaultConverters,
  ...LinkJSXConverter({
    internalDocToHref: resolveInternalDocHref
  })
});
const BASE_CLASS_NAME = 'prose prose-neutral max-w-none prose-figure:my-8 prose-img:rounded-lg prose-headings:tracking-tight';
export function PayloadRichText(t0) {
  const $ = _c(5);
  const {
    className,
    content
  } = t0;
  if (!hasRichTextRoot(content)) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = [BASE_CLASS_NAME, className].filter(Boolean);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1.join(" ");
  let t3;
  if ($[2] !== content || $[3] !== t2) {
    t3 = <RichText className={t2} converters={storefrontRichTextConverters} data={content} />;
    $[2] = content;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}